// Smart Daara - Notifications Dropdown Component
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { notificationAPI } from '../../lib/api';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { ScrollArea } from '../ui/scroll-area';
import { 
  Bell, Check, CheckCheck, BookOpen, GraduationCap, 
  CreditCard, AlertCircle, UserPlus, FileCheck, XCircle
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { fr, enUS } from 'date-fns/locale';

const NotificationIcon = ({ type }) => {
  const icons = {
    lesson_completed: <BookOpen className="w-4 h-4 text-emerald-500" />,
    course_completed: <GraduationCap className="w-4 h-4 text-emerald-600" />,
    subscription_expiring: <AlertCircle className="w-4 h-4 text-amber-500" />,
    payment_received: <CreditCard className="w-4 h-4 text-green-500" />,
    welcome: <UserPlus className="w-4 h-4 text-blue-500" />,
    enrollment_request: <UserPlus className="w-4 h-4 text-amber-500" />,
    enrollment_approved: <CheckCheck className="w-4 h-4 text-emerald-500" />,
    enrollment_rejected: <XCircle className="w-4 h-4 text-red-500" />,
    quiz_passed: <FileCheck className="w-4 h-4 text-emerald-500" />,
    quiz_failed: <XCircle className="w-4 h-4 text-red-500" />,
  };
  return icons[type] || <Bell className="w-4 h-4 text-slate-500" />;
};

export default function NotificationsDropdown() {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (open) {
      loadNotifications();
    }
  }, [open]);

  // Poll for new notifications every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      loadNotificationCount();
    }, 30000);
    
    loadNotificationCount();
    return () => clearInterval(interval);
  }, []);

  const loadNotificationCount = async () => {
    try {
      const response = await notificationAPI.getMy({ page: 1, page_size: 1 });
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error('Error loading notification count:', error);
    }
  };

  const loadNotifications = async () => {
    setLoading(true);
    try {
      const response = await notificationAPI.getMy({ page: 1, page_size: 10 });
      setNotifications(response.data.items || []);
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error('Error loading notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      await notificationAPI.markAsRead(notificationId);
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationAPI.markAllRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const handleNotificationClick = (notification) => {
    if (!notification.is_read) {
      handleMarkAsRead(notification.id);
    }
    if (notification.link) {
      navigate(notification.link);
      setOpen(false);
    }
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    try {
      return formatDistanceToNow(new Date(dateString), {
        addSuffix: true,
        locale: language === 'fr' ? fr : enUS
      });
    } catch {
      return '';
    }
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon" 
          className="relative" 
          data-testid="notifications-btn"
        >
          <Bell className="w-5 h-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-medium">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80" data-testid="notifications-dropdown">
        <div className="flex items-center justify-between px-3 py-2 border-b">
          <span className="font-semibold text-sm">
            {language === 'fr' ? 'Notifications' : 'Notifications'}
          </span>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="text-xs h-7"
              onClick={handleMarkAllRead}
            >
              <CheckCheck className="w-3 h-3 mr-1" />
              {language === 'fr' ? 'Tout marquer lu' : 'Mark all read'}
            </Button>
          )}
        </div>
        
        <ScrollArea className="h-[350px]">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : notifications.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Bell className="w-10 h-10 mx-auto mb-2 text-slate-300" />
              <p className="text-sm">
                {language === 'fr' ? 'Aucune notification' : 'No notifications'}
              </p>
            </div>
          ) : (
            notifications.map((notification) => (
              <div
                key={notification.id}
                onClick={() => handleNotificationClick(notification)}
                className={`px-3 py-3 border-b cursor-pointer transition-colors hover:bg-slate-50 ${
                  !notification.is_read ? 'bg-emerald-50/50' : ''
                }`}
                data-testid={`notification-item-${notification.id}`}
              >
                <div className="flex gap-3">
                  <div className="flex-shrink-0 mt-0.5">
                    <NotificationIcon type={notification.type} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm ${!notification.is_read ? 'font-medium' : ''}`}>
                      {language === 'fr' ? notification.title_fr : notification.title}
                    </p>
                    <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">
                      {language === 'fr' ? notification.message_fr : notification.message}
                    </p>
                    <p className="text-xs text-slate-400 mt-1">
                      {formatTime(notification.created_at)}
                    </p>
                  </div>
                  {!notification.is_read && (
                    <div className="flex-shrink-0">
                      <div className="w-2 h-2 rounded-full bg-emerald-500" />
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </ScrollArea>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
